import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import {
  Container,
  Content2Xl,
  ContentWithVerticalPadding,
} from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { motion } from "framer-motion";
//import { LogoLink } from "components/headers/light.js";
import { SectionHeading as HeadingBase } from "components/misc/Headings";
import { SectionDescription as DescriptionBase } from "components/misc/Typography";
import Header from "components/headers/light.js";

import { ReactComponent as CheckboxIcon } from "feather-icons/dist/icons/check-circle.svg";

import GoogleDownloadImageSrc from "images/GetItOnGooglePlay_English.png";
import AppleDownloadImageSrc from "images/Download_on_the_App_Store_US.svg";
import heatScreenshotImageSrc from "images/demo/MainLandingPageHero.png";
import BaseballImageSrc from "images/demo/baseballGOAT.png";
import RugbyImageSrc from "images/demo/Rugby.png";
import BasketballImageSrc from "images/demo/Basketball.png";
//import logo from "images/gslogo.png";
import ResponsiveVideoEmbed from "./helpers/ResponsiveVideoEmbed.js";

import Footer from "components/footers/FiveColumnWithInputForm.js";
import products from "./products.json";

/* Hero */
const Row = tw.div`flex`;
//const NavRow = tw(Row)`flex flex-col lg:flex-row items-center justify-between`;
const HeroRow = tw(
  Row
)`flex-col lg:flex-row justify-between items-center pt-8 lg:pt-12 pb-16 max-w-screen-2xl mx-auto flex-wrap`;

const Column = tw.div`flex-1`;

const TextColumn = tw(
  Column
)`mx-auto lg:mr-0 max-w-2xl lg:max-w-xl xl:max-w-2xl flex-shrink-0`;
const Heading = tw(
  HeadingBase
)`text-center lg:text-left text-primary-900 leading-snug`;
const Description = tw(
  DescriptionBase
)`mt-4 text-center lg:text-left lg:text-base text-gray-700 max-w-lg mx-auto lg:mx-0`;
const FeatureList = tw.ul`mt-6 leading-loose flex flex-wrap max-w-xl mx-auto lg:mx-0`;
const Feature = tw.li`mt-2 flex items-center flex-shrink-0 w-full sm:w-1/2 justify-center lg:justify-start`;
const FeatureIcon = tw(CheckboxIcon)`w-5 h-5 text-primary-500`;
const FeatureText = tw.p`ml-2 font-medium text-gray-700`;
const ImageColumn = tw(Column)`mx-auto lg:mr-0 relative mt-16 lg:mt-0 lg:ml-8`;
const ImageContainer = tw.div``;
const Image = tw.img`max-w-full rounded-t sm:rounded`;

const StyledResponsiveVideoEmbed = styled(ResponsiveVideoEmbed)`
  padding-bottom: 56.25% !important;
  padding-top: 0px !important;
  ${tw`rounded`}
  iframe {
    ${tw`rounded bg-black shadow-xl`}
  }
`;

/** New High-End Components **/
const Highlight = tw.span`text-primary-500 transition-colors duration-300 hover:text-primary-700 cursor-default`;
const Slogan = tw.h2`text-4xl sm:text-5xl font-black tracking-tighter text-center pt-10 md:pt-24 pb-10 text-gray-900`;

const GameSection = styled(ContentWithVerticalPadding)`
  ${tw`flex flex-col md:flex-row items-center justify-between gap-12 py-16`}
  ${({ reversed }) => reversed && tw`md:flex-row-reverse`}
`;

const GameText = tw.div`md:w-1/2 text-center md:text-left`;
const GameTitle = tw.h3`text-3xl font-bold text-primary-900 mb-2`;
const GameTagline = tw.p`text-gray-900 font-bold tracking-wider uppercase text-sm mb-4`;
const GameDescription = tw.p`text-gray-600 leading-relaxed mb-6`;
const GameFeatureList = tw.ul`space-y-2 mb-8 text-left inline-block md:block`;
const GameFeatureItem = tw.li`flex items-start text-gray-700`;
const GameFeatureIcon = tw(CheckboxIcon)`w-5 h-5 text-primary-500 mr-2 flex-shrink-0 mt-1`;

const ButtonContainer = tw.div`flex justify-center md:justify-start gap-4 mt-6`;
const ProductLinks = tw.div`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 py-8`;
const ProductLink = tw.a`block rounded-lg border border-gray-200 p-6 font-bold text-primary-900 hover:border-primary-500 hover:text-primary-500 transition-colors`;
const StoreButton = styled.a`
  ${tw`transition-transform duration-300 hover:scale-105`}
  img {
    ${tw`h-12 w-auto`}
  }
`;

export default ({
  features = null,
  heading = "Welcome to the wonderful world of sports games!",
  description = "Every member of our team is driven by a love for sports and a dedication to game development. We are committed not only to creating realistic and exciting gaming environments but also to fostering a positive, vibrant community to share the charm and joy of sports with you.",
}) => {
  features = features || [
    "Immersive Career Simulations",
    "Global Competitions",
    "Authentic Player Growth"
  ];

  return (
    <AnimationRevealPage disabled>
      <Header />
      <Container tw="bg-white -mx-8 -mt-8 pt-8 px-8">
        <Content2Xl>
          <HeroRow>
            <TextColumn>
              <Heading as="h1">{heading}</Heading>
              <Description>{description}</Description>
              <FeatureList>
                {features.map((feature, index) => (
                  <Feature key={index}>
                    <FeatureIcon />
                    <FeatureText>{feature}</FeatureText>
                  </Feature>
                ))}
              </FeatureList>
            </TextColumn>
            <ImageColumn>
              <ImageContainer>
                <Image src={heatScreenshotImageSrc} />
              </ImageContainer>
            </ImageColumn>
          </HeroRow>

          <section id="games" aria-label="Official game pages">
            <ProductLinks>
              {products.map(product => <ProductLink key={product.slug} href={`/games/${product.slug}.html`}>
                {product.name}<span tw="block mt-2 text-sm font-normal text-gray-600">Explore the game →</span>
              </ProductLink>)}
            </ProductLinks>
          </section>
          <Slogan>It’s All About the <Highlight>Moment</Highlight>.</Slogan>

          {/* Football GOAT */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <GameSection id="football-goat">
              <GameText>
                <GameTagline>The Career Simulation</GameTagline>
                <GameTitle><a href="/games/football-goat.html">Football GOAT</a></GameTitle>
                <GameDescription>
                  Strive to become the greatest football legend of all time. Start as a young prodigy, manage your transfers, and dominate the field.
                </GameDescription>
                <GameFeatureList>
                  <GameFeatureItem><GameFeatureIcon />Manage negotiations, contracts, and sponsors.</GameFeatureItem>
                  <GameFeatureItem><GameFeatureIcon />Enhance attributes: speed, shooting, passing, defense.</GameFeatureItem>
                  <GameFeatureItem><GameFeatureIcon />Authentic match simulation and decision making.</GameFeatureItem>
                </GameFeatureList>

                <StyledResponsiveVideoEmbed
                  url="https://www.youtube.com/embed/h2BnEpalqzo?si=3emh2ZULYRLlSbjt"
                  background="transparent"
                />

                <ButtonContainer>
                  <StoreButton href="https://play.google.com/store/apps/details?id=com.babuyo.amf.android.oversea" target="_blank" rel="noopener noreferrer">
                    <Image src={GoogleDownloadImageSrc} alt="Get it on Google Play" />
                  </StoreButton>
                  <StoreButton href="https://apps.apple.com/us/app/football-goat/id6450971694" target="_blank" rel="noopener noreferrer">
                    <Image src={AppleDownloadImageSrc} alt="Download on the App Store" />
                  </StoreButton>
                </ButtonContainer>
              </GameText>
              <div tw="md:w-1/2">
                {/* Visual balance for video */}
              </div>
            </GameSection>
          </motion.div>

          {/* Baseball GOAT */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <GameSection reversed id="baseball-goat">
              <div tw="md:w-1/2">
                <Image src={BaseballImageSrc} tw="rounded-lg shadow-xl hover:shadow-2xl transition duration-500" />
              </div>
              <GameText>
                <GameTagline>Road to the Majors</GameTagline>
                <GameTitle>
                  <a href="/games/baseball-goat.html" style={{ textDecoration: 'none', color: 'inherit' }}>Baseball GOAT</a>
                </GameTitle>
                <GameDescription>
                  From one of 168 prestigious schools to Cooperstown immortality. Experience the true essence of the diamond, from the College World Series to the Olympics.
                </GameDescription>
                <GameFeatureList>
                  <GameFeatureItem><GameFeatureIcon />Compete in the College World Series & March Madness.</GameFeatureItem>
                  <GameFeatureItem><GameFeatureIcon />Represent Team USA in the Olympics & WBC.</GameFeatureItem>
                  <GameFeatureItem><GameFeatureIcon />Live the life: 100+ events, media interactions, and drafts.</GameFeatureItem>
                </GameFeatureList>
                <div tw="mt-4 text-sm"><a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Privacy Policy</a></div>
                <ButtonContainer>
                  <StoreButton href="https://play.google.com/store/apps/details?id=com.redfalcon.baseballgoat.android" target="_blank" rel="noopener noreferrer">
                    <Image src={GoogleDownloadImageSrc} alt="Get it on Google Play" />
                  </StoreButton>
                  <StoreButton href="https://apps.apple.com/us/app/baseball-goat/id6677019784" target="_blank" rel="noopener noreferrer">
                    <Image src={AppleDownloadImageSrc} alt="Download on the App Store" />
                  </StoreButton>
                </ButtonContainer>
              </GameText>
            </GameSection>
          </motion.div>

          {/* Rugby GOAT */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <GameSection id="rugby-goat">
              <GameText>
                <GameTagline>Train. Rise. Compete.</GameTagline>
                <GameTitle>
                  <a href="/Rugby-GOAT" style={{ textDecoration: 'none', color: 'inherit' }}>Rugby GOAT</a>
                </GameTitle>
                <GameDescription>
                  From muddy school fields to the world's biggest stadiums. Create your player, master the scrums, and rise through the ranks of the UK, France, and Japan.
                </GameDescription>
                <GameFeatureList>
                  <GameFeatureItem><GameFeatureIcon />Face 2,000+ real stars.</GameFeatureItem>
                  <GameFeatureItem><GameFeatureIcon />Dynamic stats evolution based on training.</GameFeatureItem>
                  <GameFeatureItem><GameFeatureIcon />Compete in the World Cup & Olympic Sevens.</GameFeatureItem>
                </GameFeatureList>
                <div tw="mt-4 text-sm"><a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Privacy Policy</a></div>
                <ButtonContainer>
                  <StoreButton href="https://play.google.com/store/apps/details?id=com.redfalcon.rugbygoat.android" target="_blank" rel="noopener noreferrer">
                    <Image src={GoogleDownloadImageSrc} alt="Get it on Google Play" />
                  </StoreButton>
                  <StoreButton href="https://apps.apple.com/us/app/rugby-goat/idexample" target="_blank" rel="noopener noreferrer">
                    <Image src={AppleDownloadImageSrc} alt="Download on the App Store" />
                  </StoreButton>
                </ButtonContainer>
              </GameText>
              <div tw="md:w-1/2">
                <Image src={RugbyImageSrc} tw="rounded-lg shadow-xl hover:shadow-2xl transition duration-500" />
              </div>
            </GameSection>
          </motion.div>

          {/* Basketball GOAT */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <GameSection reversed id="basketball-goat">
              <div tw="md:w-1/2">
                <Image src={BasketballImageSrc} tw="rounded-lg shadow-xl hover:shadow-2xl transition duration-500" />
              </div>
              <GameText>
                <GameTagline>Create Your Legacy</GameTagline>
                <GameTitle><a href="/games/basketball-goat.html">Basketball GOAT</a></GameTitle>
                <GameDescription>
                  This isn't just a game; it's your path to greatness. From the squeak of sneakers on hardwood to the buzzer-beaters that define eras.
                </GameDescription>
                <GameFeatureList>
                  <GameFeatureItem><GameFeatureIcon />Realistic path: High School to Pro Leagues (USA, Euro, Asia).</GameFeatureItem>
                  <GameFeatureItem><GameFeatureIcon />Deep simulation: Injuries, drafts, and off-court drama.</GameFeatureItem>
                  <GameFeatureItem><GameFeatureIcon />Every choice shapes your reputation and legacy.</GameFeatureItem>
                </GameFeatureList>
                <div tw="mt-4 text-sm"><a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Privacy Policy</a></div>
                <ButtonContainer>
                  <StoreButton href="https://play.google.com/store/apps/details?id=com.redfalcon.basketballgoat.android" target="_blank" rel="noopener noreferrer">
                    <Image src={GoogleDownloadImageSrc} alt="Get it on Google Play" />
                  </StoreButton>
                  <StoreButton href="https://apps.apple.com/us/app/basketball-goat/id6756869790" target="_blank" rel="noopener noreferrer">
                    <Image src={AppleDownloadImageSrc} alt="Download on the App Store" />
                  </StoreButton>
                </ButtonContainer>
              </GameText>
            </GameSection>
          </motion.div>

          <div className="App">
            <Footer />
          </div>
        </Content2Xl>
      </Container>
    </AnimationRevealPage>
  );
};
